<script setup lang="ts">
import { ref, toRef, computed } from 'vue'
import { useQueryParam, stringParam } from 'vue-use-query-param'

import {
  CloseCircleFilled,
  CheckCircleFilled,
  SaveOutlined,
  DisconnectOutlined,
  EditOutlined,
  InfoCircleFilled,
} from '@ant-design/icons-vue'

import { LinkedFarmInvitationSentT } from '@ruminati/types_v2/linked_farm_invitation'
import { useLinkedFarmsEmissionsReports } from '@/composables/emissionsReports'
import { useResourcePermissions } from '@/composables/permissions'
import { useOrganisation } from '@/composables/organisations'

import NestedRounded from '../Base/NestedRounded.vue'
import { useLinkedFarmInvitationsWithLinkedFarms } from '@/composables/linkedFarmInvitations'
import { reportYears } from '@/utilities/reports'
import { formatDateTimestampAsReadable } from '@/utilities/dates'
import { visionSharingScopeLabels } from '@/utilities/vision'
import { VisionSharingScopesT } from '@ruminati/types_v2/vision'

const selectedLinkedBusinessId = useQueryParam('selectedLinkedBusinessId', stringParam())

const props = defineProps<{
  invitation: LinkedFarmInvitationSentT
  setEditInviteId: (id: string) => void
}>()

const { currentOrg } = useOrganisation()
const { pendingAndLinkedFarmsByOrgMap, deleteInvitation, patchInvitation } =
  useLinkedFarmInvitationsWithLinkedFarms()
const { emissionsReportsByPropertyMap } = useLinkedFarmsEmissionsReports()

const linkedBusinessesForInviteWithLatestReport = computed(() => {
  if (!props.invitation.primaryOrganisation?.id) return []
  const lf = pendingAndLinkedFarmsByOrgMap.value.get(props.invitation.primaryOrganisation?.id)
  return (lf ?? [])
    .filter((lf) => lf.linkedFarm.status !== 'shared')
    .map((lf) => {
      const reports = emissionsReportsByPropertyMap.value?.get(lf.linkedFarm.propertyId)
      return {
        ...lf,
        hasCompleteReportForLatestYear: reports && reports[reportYears[0]],
      }
    })
})

const confirmText = computed(() => {
  let basicText = 'Are you sure you want to remove this Invitation?'
  if (linkedBusinessesForInviteWithLatestReport.value.length > 0)
    basicText = `${basicText} Linked Businesses already established will remain however the user will not be able to share any new data with you.`
  return basicText
})

const actioned = computed(() => {
  return 'id' in props.invitation.recipientDetails
})

const { allowedToDelete, allowedToEdit } = useResourcePermissions(
  toRef(() => props.invitation.invite.id),
)
const updateSharingTypes = ref(false)
const updatedScopes = ref(props.invitation.invite.requestedScopes.slice())

const availableScopesWithStatus = computed(() => {
  return currentOrg.value?.preferences?.visionOrg?.availableSharingScopes.map((s) => {
    return {
      scope: s as VisionSharingScopesT,
      shared: props.invitation.invite.requestedScopes.includes(s),
    }
  })
})

const availableOptions = computed(() => {
  return currentOrg.value?.preferences?.visionOrg?.availableSharingScopes.map((s) => {
    return {
      value: s,
      label: visionSharingScopeLabels[s],
    }
  })
})
</script>

<template>
  <NestedRounded>
    <template #title>
      <p class="markable text-xl">
        {{
          invitation.primaryOrganisation
            ? invitation.primaryOrganisation?.name
            : invitation.recipientDetails.email
        }}
      </p>
    </template>

    <template #action-top>
      <a-flex align="center" class="gap-x-4">
        <p v-if="invitation.invite.clientReferenceId" class="text-rumi-grey-700 markable">
          Reference ID: {{ invitation.invite.clientReferenceId }}
          <EditOutlined
            v-if="allowedToEdit"
            class="text-rumi-grey-700 cursor-pointer text-sm"
            @click="
              () => {
                if (!allowedToEdit) return
                setEditInviteId(invitation.invite.id)
              }
            "
          />
        </p>

        <p
          v-else-if="
            currentOrg?.preferences?.visionOrg?.useClientReferenceId &&
            !invitation.invite.clientReferenceId
          "
        >
          <span
            class="text-sm"
            :class="{
              'text-rumi-grey-400': !allowedToEdit,
              'text-rumi-grey-700': allowedToEdit,
              'cursor-pointer': allowedToEdit,
            }"
            @click="
              () => {
                if (!allowedToEdit) return
                setEditInviteId(invitation.invite.id)
              }
            "
          >
            Add Reference ID <EditOutlined />
          </span>
        </p>

        <a-popconfirm
          :title="confirmText"
          placement="left"
          overlay-class-name="w-[350px]"
          :disabled="!allowedToDelete"
          @confirm="
            async () => {
              await deleteInvitation(invitation.invite.id)
            }
          "
        >
          <a-button type="link" danger class="pr-0" size="small" :disabled="!allowedToDelete">
            <DisconnectOutlined />
            Disconnect
          </a-button>
        </a-popconfirm>
      </a-flex>
    </template>

    <template #inner>
      <a-flex>
        <a-flex v-if="actioned" vertical flex="0 0 25%">
          <p class="text-rumi-grey-600 mb-1 text-sm">Invite Recipient</p>
          <p
            v-if="'firstName' in invitation.recipientDetails"
            class="text-rumi-grey-700 leading-[1.5]"
          >
            <span class="markable"
              >{{ invitation.recipientDetails.firstName }}
              {{ invitation.recipientDetails.lastName }}</span
            >
            <br />
            <span class="markable text-sm">
              {{ invitation.recipientDetails.email }}
            </span>
          </p>
          <p v-else>Not Available</p>
        </a-flex>
        <a-flex v-if="!actioned" vertical flex="auto">
          <p class="text-rumi-grey-600 mb-1 text-sm">
            Invite sent to {{ invitation.recipientDetails.email }} but they have not yet created an
            account on the Ruminati Platform.
          </p>
        </a-flex>

        <a-flex v-if="actioned" vertical flex="0 0 45%">
          <p class="text-rumi-grey-600 mb-1 text-sm">Linked Businesses</p>
          <a-flex wrap="wrap" class="gap-x-[8px] gap-y-[2px]">
            <a-flex
              v-for="property in linkedBusinessesForInviteWithLatestReport"
              :key="property.linkedFarm.id"
              vertical
              flex="0 0 auto"
              class="hasPointer p-y-1 border-rd-md"
              :class="{
                'bg-rumi-brand-green-800': property.hasCompleteReportForLatestYear,
                'p-x-[8px]': property.hasCompleteReportForLatestYear,
              }"
            >
              <p
                class="border-b-dashed markable border-b-1 text-sm leading-[1.1]"
                :class="{
                  'text-white': property.hasCompleteReportForLatestYear,
                }"
                @click="
                  () => {
                    selectedLinkedBusinessId = property.linkedFarm.propertyId
                  }
                "
              >
                {{ property.property.propertyName }}
              </p>
            </a-flex>
            <a-flex
              v-if="linkedBusinessesForInviteWithLatestReport.length === 0"
              flex="0 0 auto"
              class="mr-3 py-[4px]"
            >
              <p class="text-sm leading-[1.1]">No Enterprises</p>
              <p />
            </a-flex>
          </a-flex>
        </a-flex>

        <a-flex vertical flex="1 0 auto" align="flex-end">
          <p class="text-rumi-grey-600 text-sm">
            Invited {{ formatDateTimestampAsReadable(invitation.invite.created) }}
          </p>

          <a-popover
            placement="left"
            :overlay-style="{
              width: '350px',
            }"
            @open-change="
              () => {
                updateSharingTypes = false
                updatedScopes = props.invitation.invite.requestedScopes.slice()
              }
            "
          >
            <template #content>
              <a-flex vertical class="gap-x-1">
                <a-flex class="mb-1">
                  <p>
                    You have requested the following types of information from
                    {{ invitation.primaryOrganisation?.name }}
                  </p>
                </a-flex>

                <div v-if="!updateSharingTypes">
                  <a-flex
                    v-for="(scope, idx) in availableScopesWithStatus"
                    :key="idx"
                    align="center"
                    class="pl-1"
                  >
                    <CheckCircleFilled
                      v-if="scope.shared"
                      class="text-rumi-green-success-500 text-xs"
                    />
                    <CloseCircleFilled v-else class="text-rumi-red-700 text-xs" />
                    <p class="ml-1">
                      {{ visionSharingScopeLabels[scope.scope] }}
                    </p>
                  </a-flex>
                </div>
                <div v-else>
                  <a-checkbox-group v-model:value="updatedScopes" :options="availableOptions" />
                </div>

                <a-flex justify="flex-end" class="mt-3 gap-x-3">
                  <a-button
                    v-if="updateSharingTypes"
                    size="small"
                    @click="
                      () => {
                        updateSharingTypes = false
                        updatedScopes = props.invitation.invite.requestedScopes.slice()
                      }
                    "
                  >
                    Cancel
                  </a-button>

                  <a-button
                    v-if="updateSharingTypes"
                    size="small"
                    type="primary"
                    @click="
                      () => {
                        patchInvitation(props.invitation.invite.id, {
                          requestedScopes: updatedScopes,
                        })
                      }
                    "
                  >
                    <SaveOutlined />
                    Save
                  </a-button>

                  <a-button
                    v-if="!updateSharingTypes"
                    size="small"
                    @click="
                      () => {
                        updateSharingTypes = true
                      }
                    "
                  >
                    Update
                  </a-button>
                </a-flex>
              </a-flex>
            </template>
            <p class="text-rumi-grey-700 mt-2 cursor-pointer text-sm">
              Requesting {{ invitation.invite.requestedScopes.length }} types of data
              <InfoCircleFilled class="text-rumi-grey-600" />
            </p>
          </a-popover>
        </a-flex>
      </a-flex>
    </template>
  </NestedRounded>
</template>
